import * as Sentry from "@sentry/react"

import {
  type ClientConnectionState,
  type QueuedTransportFailure,
  bufferTransportFailure
} from "./offline-transport-buffer"

export interface TransportFailureContext {
  url?: string | null
  method?: string | null
  durationMs?: number | null
}

interface NormalizedTransportFailureContext {
  url: string
  method: string
  durationMs: number
}

interface RateLimitState {
  total: number
  lastReportedAtByEndpoint: Record<string, number>
}

interface TransportFailureReporterOptions {
  capture?: (context: NormalizedTransportFailureContext) => void
  getStorage?: () => Storage | null
  now?: () => number
  maxEventsPerSession?: number
  endpointCooldownMs?: number
  bufferFailure?: (failure: QueuedTransportFailure) => void | Promise<void>
  getConnectionState?: () => ClientConnectionState
}

const RATE_LIMIT_STORAGE_KEY = "ead.transport-failure-rate-limit.v1"
const DEFAULT_MAX_EVENTS_PER_SESSION = 20
const DEFAULT_ENDPOINT_COOLDOWN_MS = 60_000

const getBrowserConnectionState = (): ClientConnectionState => {
  if (typeof navigator === "undefined") return "unknown"
  return navigator.onLine ? "online" : "offline"
}

const emptyRateLimitState = (): RateLimitState => ({
  total: 0,
  lastReportedAtByEndpoint: {}
})

export const normalizeTransportEndpoint = (url?: string | null): string => {
  const path = (url ?? "unknown")
    .replace(/^https?:\/\/[^/]+\/?/i, "")
    .split(/[?#]/, 1)[0]
    .replace(/^\/+/, "")

  return (path || "unknown")
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
      ":id"
    )
    .replace(/\/(\d+)(?=\/|$)/g, "/:id")
    .slice(0, 255)
}

const normalizeContext = (
  context: TransportFailureContext
): NormalizedTransportFailureContext => ({
  url: normalizeTransportEndpoint(context.url),
  method: (context.method ?? "unknown").toUpperCase(),
  durationMs: Math.max(0, Math.round(context.durationMs ?? 0))
})

const captureToSentry = (context: NormalizedTransportFailureContext): void => {
  Sentry.captureMessage("Handled transport failure", {
    level: "warning",
    tags: {
      endpoint: context.url,
      method: context.method
    },
    extra: {
      endpoint: context.url,
      method: context.method,
      duration_ms: context.durationMs
    },
    fingerprint: ["handled-transport-failure", context.url]
  })
}

const readState = (storage: Storage | null): RateLimitState => {
  if (!storage) return emptyRateLimitState()

  try {
    const parsed = JSON.parse(storage.getItem(RATE_LIMIT_STORAGE_KEY) ?? "null")
    if (
      typeof parsed?.total === "number" &&
      typeof parsed?.lastReportedAtByEndpoint === "object"
    ) {
      return parsed as RateLimitState
    }
  } catch {
    // A malformed or inaccessible session entry should not break requests.
  }

  return emptyRateLimitState()
}

const writeState = (storage: Storage | null, state: RateLimitState): void => {
  if (!storage) return

  try {
    storage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Sentry reporting stays best-effort when sessionStorage is unavailable.
  }
}

export const createTransportFailureReporter = ({
  capture = captureToSentry,
  getStorage = () =>
    typeof window === "undefined" ? null : window.sessionStorage,
  now = Date.now,
  maxEventsPerSession = DEFAULT_MAX_EVENTS_PER_SESSION,
  endpointCooldownMs = DEFAULT_ENDPOINT_COOLDOWN_MS,
  bufferFailure = bufferTransportFailure,
  getConnectionState = getBrowserConnectionState
}: TransportFailureReporterOptions = {}) => {
  const memoryState = emptyRateLimitState()

  return (failure: TransportFailureContext): boolean => {
    const context = normalizeContext(failure)
    let storage: Storage | null = null
    try {
      storage = getStorage()
    } catch {
      // Browsers can deny sessionStorage even when `window` is available.
    }
    const state = storage ? readState(storage) : memoryState
    const timestamp = now()
    try {
      void Promise.resolve(
        bufferFailure({
          connection_state: getConnectionState(),
          endpoint: context.url,
          timestamp: new Date(timestamp).toISOString()
        })
      ).catch(() => {})
    } catch {
      // Durable buffering is best-effort and must not replace the request error.
    }
    const lastReportedAt = state.lastReportedAtByEndpoint[context.url]

    if (
      state.total >= maxEventsPerSession ||
      (lastReportedAt !== undefined &&
        timestamp - lastReportedAt < endpointCooldownMs)
    ) {
      return false
    }

    try {
      capture(context)
    } catch {
      return false
    }

    state.total += 1
    state.lastReportedAtByEndpoint[context.url] = timestamp
    writeState(storage, state)
    return true
  }
}

export const reportTransportFailure = createTransportFailureReporter()
